import { DateTime, Info } from 'luxon'

export type DateInput = Date | string | null | undefined

const LOCALE = 'es'

/** Normaliza puntos que Luxon a veces añade en meses cortos (`ene.` → `ene`). */
const cleanLocaleText = (value: string) => value.replace(/\./g, '')

/**
 * Parsea fechas de API / formularios a `DateTime` (zona local).
 * - ISO con hora (`…T…`) → `fromISO`
 * - Solo fecha (`YYYY-MM-DD`) → día calendario local (sin shift por UTC)
 */
export const parseDate = (value?: DateInput): DateTime | null => {
    if (!value) return null
    if (value instanceof Date) {
        const dt = DateTime.fromJSDate(value)
        return dt.isValid ? dt : null
    }
    const text = String(value).trim()
    if (!text) return null
    if (text.includes('T')) {
        const dt = DateTime.fromISO(text)
        return dt.isValid ? dt : null
    }
    const dt = DateTime.fromISO(text.slice(0, 10), { zone: 'local' })
    return dt.isValid ? dt : null
}

/** Compat: `Date` nativo o `null` (para comparaciones existentes). */
export const toDate = (value?: DateInput): Date | null => {
    const dt = parseDate(value)
    return dt ? dt.toJSDate() : null
}

/** `15 ene 2024` */
export const formatSpokenDate = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    return cleanLocaleText(dt.setLocale(LOCALE).toFormat('d LLL yyyy'))
}

/** `15 ene` */
export const formatDayMonth = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    return cleanLocaleText(dt.setLocale(LOCALE).toFormat('d LLL'))
}

/** `Lunes` */
export const formatWeekdayName = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    const text = dt.setLocale(LOCALE).toFormat('cccc')
    return text.charAt(0).toUpperCase() + text.slice(1)
}

/** `ene 2024` */
export const formatMonthYear = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    return cleanLocaleText(dt.setLocale(LOCALE).toFormat('LLL yyyy'))
}

/** `Octubre 2026` */
export const formatLongMonthYear = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    const text = dt.setLocale(LOCALE).toFormat('LLLL yyyy')
    return text.charAt(0).toUpperCase() + text.slice(1)
}

/** `['Lunes', …]` empezando en lunes. `short` devuelve `Lun`, `Mar`, … */
export const weekdayNames = (length: 'long' | 'short' | 'narrow' = 'long'): string[] =>
    Info.weekdays(length, { locale: LOCALE }).map((name) => {
        const clean = cleanLocaleText(name)
        return clean.charAt(0).toUpperCase() + clean.slice(1)
    })

/** Años cumplidos a hoy (edad). */
export const yearsSince = (value?: DateInput): number | null => {
    const dt = parseDate(value)
    if (!dt) return null
    return Math.floor(DateTime.now().diff(dt.startOf('day'), 'years').years)
}

/** `15 ene 1990 · 34 años` */
export const formatWithAge = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    const years = Math.floor(DateTime.now().diff(dt.startOf('day'), 'years').years)
    const spoken = formatSpokenDate(value)
    if (!spoken) return null
    return years >= 0 ? `${spoken} · ${years} años` : spoken
}

/** `Hoy, 14:30` o `15 ene 2024` */
export const formatActivityDate = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    if (dt.hasSame(DateTime.now(), 'day')) {
        return `Hoy, ${dt.toFormat('HH:mm')}`
    }
    return cleanLocaleText(dt.setLocale(LOCALE).toFormat('dd LLL yyyy'))
}

/** `15 ene 24` */
export const formatCompactDate = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    if (!dt) return null
    return cleanLocaleText(dt.setLocale(LOCALE).toFormat('dd LLL yy'))
}

/** `15/01/2024` */
export const formatSlashDate = (value?: DateInput): string | null => {
    if (!value) return null
    const dt = parseDate(value)
    if (!dt) return String(value).slice(0, 10)
    return dt.toFormat('dd/MM/yyyy')
}

/** `['Enero', 'Febrero', …]` (índice 0 = enero). */
export const monthNames = (): string[] =>
    Info.months('long', { locale: LOCALE }).map((name) => name.charAt(0).toUpperCase() + name.slice(1))

export const currentYear = (): number => DateTime.now().year

/** `15/01/2024 14:30` a partir de `yyyy-MM-dd HH:mm:ss` (LocalDateTime del API) o ISO. */
export const formatSlashDateTime = (value?: DateInput): string | null => {
    if (!value) return null
    const text = String(value).trim()
    const dt = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)
        ? DateTime.fromFormat(text.slice(0, 19), text.length >= 19 ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd HH:mm')
        : parseDate(value)
    return dt?.isValid ? dt.toFormat('dd/MM/yyyy HH:mm') : null
}

const parseLocalDateTime = (value?: DateInput): DateTime | null => {
    if (!value) return null
    const text = String(value).trim()
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(text)) {
        const dt = DateTime.fromFormat(text.slice(0, 19), 'yyyy-MM-dd HH:mm:ss')
        return dt.isValid ? dt : null
    }
    return parseDate(value)
}

/** `15 ene 2026 · 10:30` a partir de un LocalDateTime del API. */
export const formatSpokenDateTime = (value?: DateInput): string | null => {
    const dt = parseLocalDateTime(value)
    if (!dt) return null
    return `${cleanLocaleText(dt.setLocale(LOCALE).toFormat('d LLL yyyy'))} · ${dt.toFormat('HH:mm')}`
}

/** `10:30` a partir de un LocalDateTime del API. */
export const formatTimeOfDay = (value?: DateInput): string | null => {
    const dt = parseLocalDateTime(value)
    return dt ? dt.toFormat('HH:mm') : null
}

/** LocalDateTime del API → valor de `<input type="date">` (`yyyy-MM-dd`). */
export const toDateInput = (value?: DateInput): string => parseLocalDateTime(value)?.toISODate() || ''

/** Valor de `<input type="date">` → LocalDateTime del API al final de ese día (`yyyy-MM-dd 23:59:59`). */
export const endOfDayFromDateInput = (value?: string | null): string | null => {
    const dt = parseDate(value)
    return dt ? dt.endOf('day').toFormat('yyyy-MM-dd HH:mm:ss') : null
}

/** Mes actual en texto (`octubre`) y año actual. */
export const currentMonthName = (): string => DateTime.now().setLocale(LOCALE).toFormat('LLLL')

/** `ene 26` a partir de una llave de mes `yyyyMM`. */
export const formatMonthKey = (key?: string | null): string => {
    const dt = key ? DateTime.fromFormat(key, 'yyyyMM') : null
    return dt?.isValid ? cleanLocaleText(dt.setLocale(LOCALE).toFormat('LLL yy')) : key || ''
}

/** `yyyy-MM` del mes actual desplazado `months` meses. */
export const monthIso = (months = 0): string => DateTime.now().plus({ months }).toFormat('yyyy-MM')

/** Primer y último día (`YYYY-MM-DD`) de un rango de meses `yyyy-MM`, opcionalmente desplazado en años. */
export const monthRangeIso = (fromMonth: string, toMonth: string, shiftYears = 0) => {
    const from = DateTime.fromFormat(fromMonth, 'yyyy-MM').plus({ years: shiftYears }).startOf('month')
    const to = DateTime.fromFormat(toMonth, 'yyyy-MM').plus({ years: shiftYears }).endOf('month')
    return { from: from.isValid ? from.toISODate()! : '', to: to.isValid ? to.toISODate()! : '' }
}

/** Llaves `yyyyMM` de cada mes entre dos meses `yyyy-MM` (incluidos). */
export const monthKeys = (fromMonth: string, toMonth: string): string[] => {
    let cursor = DateTime.fromFormat(fromMonth, 'yyyy-MM').startOf('month')
    const end = DateTime.fromFormat(toMonth, 'yyyy-MM').startOf('month')
    if (!cursor.isValid || !end.isValid || cursor > end) return []
    const keys: string[] = []
    while (cursor <= end && keys.length < 120) {
        keys.push(cursor.toFormat('yyyyMM'))
        cursor = cursor.plus({ months: 1 })
    }
    return keys
}

/** Llave `yyyyMM` desplazada `years` años. */
export const shiftMonthKey = (key: string, years: number): string => DateTime.fromFormat(key, 'yyyyMM').plus({ years }).toFormat('yyyyMM')

/** Fecha `YYYY-MM-DD` de hace `years` años. */
export const isoYearsAgo = (years: number): string => DateTime.now().minus({ years }).toISODate()!

/** `20261017145800`, para prefijar nombres de archivo subidos. */
export const fileTimestamp = (): string => DateTime.now().toFormat('yyyyMMddHHmmss')

export const toIsoDate = (value?: DateInput): string | null => {
    const dt = parseDate(value)
    return dt ? dt.toISODate() : null
}

export const todayIso = (): string => DateTime.now().toISODate()!

/** Parte un rango `YYYY-MM-DD` en tramos que no cruzan de mes. */
export const monthSegmentsIso = (from: string, to: string): Array<{ from: string; to: string }> => {
    let cursor = parseDate(from)
    const end = parseDate(to)
    const segments: Array<{ from: string; to: string }> = []
    while (cursor && end && cursor <= end && segments.length < 24) {
        const monthEnd = cursor.endOf('month').startOf('day')
        const segmentEnd = monthEnd < end ? monthEnd : end
        segments.push({ from: cursor.toISODate()!, to: segmentEnd.toISODate()! })
        cursor = segmentEnd.plus({ days: 1 })
    }
    return segments
}

export const shiftIso = (days: number): string => DateTime.now().plus({ days }).toISODate()!

/** Días desde hoy (fecha calendario local). Negativo = pasado. */
export const daysFromToday = (value?: DateInput): number | null => {
    const dt = parseDate(value)?.startOf('day')
    if (!dt) return null
    return Math.round(dt.diff(DateTime.now().startOf('day'), 'days').days)
}

/** Partes de UI tipo calendario: `{ d: '15', m: 'ene', time }`. */
export const eventParts = (value?: DateInput): { d: string; m: string; time: number } | null => {
    const dt = parseDate(value)
    if (!dt) return null
    return {
        d: String(dt.day),
        m: cleanLocaleText(dt.setLocale(LOCALE).toFormat('LLL')),
        time: dt.toMillis(),
    }
}
